import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchProducts, type Product } from '@/services/api';

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts(1, 4)
      .then((res) => setProducts(res.items))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <p className="text-stone-400">Loading products...</p>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <span className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3 block">Our Products</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
              Signature<br />Collection
            </h2>
          </div>
          <div className="md:max-w-xs">
            <p className="text-stone-500 text-sm leading-relaxed mb-4">
              Each piece is crafted with meticulous attention to detail, using only the finest materials for lasting performance.
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-stone-900 text-sm font-semibold tracking-wider uppercase border-b border-stone-900 pb-0.5 hover:text-amber-700 hover:border-amber-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Products
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
          {products.map((product) => {
            const img = product.images?.[0]?.url || '';
            return (
              <Link
                key={product.id}
                to={`/products?id=${product.id}`}
                className="group relative rounded-2xl overflow-hidden bg-white cursor-pointer block"
              >
                {/* Image */}
                <div className="relative w-full h-72 overflow-hidden bg-stone-100">
                  {img && (
                    <img
                      src={img}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  )}
                  {product.tag && (
                    <span className="absolute top-3 left-3 bg-stone-900/80 text-white text-xs font-medium px-3 py-1 rounded-full">
                      {product.tag}
                    </span>
                  )}
                </div>
                {/* Info */}
                <div className="p-5">
                  <span className="text-amber-600 text-xs font-medium tracking-wider uppercase">{product.category_name}</span>
                  <h3 className="text-stone-900 font-semibold text-base mt-1 mb-2">{product.name}</h3>
                  <p className="text-stone-500 text-xs leading-relaxed line-clamp-2">{product.description || product.material}</p>
                  <div className="mt-4 flex items-center gap-1 text-stone-900 text-xs font-semibold tracking-wider uppercase group-hover:text-amber-700 transition-colors">
                    <span>View Details</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
